/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef, Row, RowSelectionState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { useDebounce } from "../../../../hooks/helpers";
import {
    useExportNotes,
    useSkipSupervisorReview,
} from "../../../../hooks/mutations/note";
import useToast from "../../../../hooks/useToast";
import { useExportPdfs } from "../../../../hooks/queries";
import { useFetchClientNotes } from "../../hooks/clients.queries";
import {
    NoteLabels,
    NoteStatus,
    NoteTypes,
} from "../../../notes/types/notes.types";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import ListState from "../../../../components/ListState/ListState";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { IClientNote } from "../../types/clients.types";
import {
    getFlaggedNoteStatusTag,
    getNoteStatusTag,
    getNoteTypeTextWithTag,
    getPdfExportableNoteIds,
    isNotePdfExportable,
} from "../../../notes/helpers/notes.helpers";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";
import { UserRole } from "../../../user-management/types/user-management.types";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import Loader from "../../../../shared-ui/Loader/Loader";
import ExportLoaderAndText from "../../../../shared-ui/ExportLoaderAndText/ExportLoaderAndText";
import { useCanCreateNote } from "../../../notes/hooks/useCanCreateNote";

export default function ClientNotesList() {
    const [jobId, setJobId] = React.useState<string | null>(null);
    const [showLoader, setShowLoader] = React.useState(false);
    const [showSkipReviewDialog, setShowSkipReviewDialog] =
        React.useState(false);
    const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(
        {}
    );
    const [pdfCount, setPdfCount] = React.useState(0);

    const [searchParams, setSearchParams] = useSearchParams();

    // Get filters from params
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const searchStringFilter = searchParams.get("search_string") || "";
    const typeFilter = searchParams.getAll("type[]");
    const showArchivedFilter = searchParams.get("show_archived") || "false";
    const showFlaggedNotesFilter = searchParams.get("show_flagged") || "true";

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    const navigate = useNavigate();

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    // Use debounce hook for provider's name filter input
    const debouncedSearchStringFilter = useDebounce(
        searchStringFilter || "",
        500
    );

    const sortableColumns = [
        "date_of_service",
        "client_name",
        "provider_name",
        "created_at",
        "author",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const showArchivedFilterForApi =
        showArchivedFilter === "all" ? "" : showArchivedFilter;

    const screenIsGreaterThan450 = useMediaQuery("(min-width: 450px)");

    const canViewFlaggedStatus = useDoesUserHaveRole([
        UserRole.AUDITOR,
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
        UserRole.CODER,
        UserRole.SUPER_ADMIN,
    ]);
    const canCreateNote = useCanCreateNote();

    // Hook for fetching notes
    const { data, error, isLoading, isSuccess } = useFetchClientNotes(
        clientId,
        {
            search_string: debouncedSearchStringFilter,
            to_date: toDateFilter,
            from_date: fromDateFilter,
            type: typeFilter,
            page: pageFilter,
            limit: limitFilter,
            show_archived: showArchivedFilterForApi,
            sort_attribute:
                searchParams.get("sort_attr") || handleConfigAttribute(),
            sort_order: searchParams.get("sort_order") || sortConfig.direction,
            show_flagged_notes: showFlaggedNotesFilter,
        }
    );

    const selectedNotesDetails = data?.data.filter((_, idx) =>
        Boolean(rowSelection[idx])
    );
    const hasExportableSelectedNote =
        selectedNotesDetails?.some(isNotePdfExportable) ?? false;

    const canSkipSupervisorReview = useDoesUserHavePermission([
        UserPermisions.NOTE_SUPERVISOR_REVIEW_SKIP,
    ]);

    const showBulkSkipSupervisorReviewBtn =
        canSkipSupervisorReview &&
        (selectedNotesDetails?.length ?? 0) > 0 &&
        selectedNotesDetails?.every(
            (note) =>
                note.status === NoteStatus.PENDING_SUPERVISOR_REVIEW &&
                note.is_archived === false
        );

    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleSetTypeFilter = (type: string) => {
        if (typeFilter.includes(type)) {
            searchParams.delete("type[]", type);
        } else {
            searchParams.append("type[]", type);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetArchivedNotesFilter = (archived: string) => {
        if (archived === "") searchParams.delete("show_archived");
        else searchParams.set("show_archived", archived);
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFlaggedStatusFilterChange = (status: string) => {
        searchParams.set("show_flagged", status);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetPageFilter = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleSetLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const getArchivedNoteFilterToDisplay = () => {
        if (showArchivedFilter === "true") {
            return "Archived notes";
        }
        if (showArchivedFilter === "false") {
            return "Unarchived notes";
        }

        return "Show all notes";
    };

    const getFlaggedNotesFilterTextToDisplay = () => {
        if (showFlaggedNotesFilter === "true") {
            return "Show flagged notes";
        }
        return "Hide flagged notes";
    };

    const exportNotes = useExportNotes();

    const canExportNotes = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const { toast } = useToast();

    const handleExportNotes = (noteId?: string) => {
        const notesToExport: IClientNote[] = noteId
            ? data?.data.filter((note) => note.note_id === noteId) ?? []
            : selectedNotesDetails ?? [];
        const sessionNoteIds = getPdfExportableNoteIds(notesToExport);

        if (sessionNoteIds.length === 0) {
            toast({
                mode: "warning",
                message: "The selected notes are unavailable for PDF export",
            });
            return;
        }

        const dataToSend = { session_note_ids: sessionNoteIds };

        exportNotes.mutate(dataToSend, {
            onSuccess: async (res) => {
                setShowLoader(true);
                setRowSelection({});
                await new Promise<void>((resolve) => {
                    const timer = setTimeout(() => {
                        clearTimeout(timer);
                        setJobId(res.data.export_job_id);
                        setPdfCount(res.data.expected_pdf_count);
                        resolve();
                    }, 6000);
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.message || "Could not export notes at this time",
                });
            },
        });
    };

    const skipSupervisorReviewMutation = useSkipSupervisorReview();

    const queryClient = useQueryClient();

    const handleSkipSupervisorReview = () => {
        const noteIds = (selectedNotesDetails || []).map(
            (note) => note.note_id
        );

        skipSupervisorReviewMutation.mutate(noteIds, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "notes"],
                });

                toast({
                    mode: "success",
                    message: res.message || "Supervisor review skipped",
                });

                setRowSelection({});
                setShowSkipReviewDialog(false);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not skip supervisor review at this time",
                });
            },
        });
    };

    const exportNote = useExportPdfs(
        {
            export_job_id: jobId as string,
            pdf_type: "session_note",
            expected_pdf_count: pdfCount,
        },
        Boolean(jobId)
    );

    if (jobId && !exportNote.isLoading) {
        setJobId(null);
        setShowLoader(false);
    }

    const handleRowClick = (row: Row<IClientNote>) => {
        searchParams.set("clientId", clientId);
        navigate(`/notes/${clientId}/${row.original.note_id}`);
    };

    // Table columns
    const columns: ColumnDef<IClientNote>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            accessorKey: "date_of_service",
            header: "DATE OF SERVICE",
            cell: ({ row }) => formatDate(row.original.date_of_service),
        },
        {
            accessorKey: "note_type",
            header: "Note Type",
            cell: ({ row }) => getNoteTypeTextWithTag(row.original.note_type),
        },
        {
            accessorKey: "status",
            header: "Note Status",
            cell: ({ row }) => (
                <span>{getNoteStatusTag(row.original.status)}</span>
            ),
        },
        {
            header: "Clinician",
            accessorKey: "author",
            cell: ({ row }) =>
                row.original.author
                    ? displayNameInRightFormat({
                          firstName: row.original.author.first_name,
                          lastName: row.original.author.last_name,
                      })
                    : "--",
        },
        {
            accessorKey: "flagged_for_audit_status",
            header: "Flagged Status",
            cell: ({ row }) =>
                row.original.flagged_for_audit_status
                    ? getFlaggedNoteStatusTag(
                          row.original.flagged_for_audit_status
                      )
                    : "--",
        },
        {
            accessorKey: "created_at",
            header: "CREATED ON",
            cell: ({ row }) => formatDate(row.original.created_at),
        },
        {
            accessorKey: "note_id",
            header: "",
            cell: ({ row }) =>
                canExportNotes && isNotePdfExportable(row.original) ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleExportNotes(row.original.note_id);
                                }}
                            >
                                Export to pdf
                            </DropdownItem>
                        </DropdownContent>
                    </Dropdown>
                ) : null,
        },
    ];

    return (
        <>
            <Dialog open={showLoader}>
                <DialogContent
                    title="Export in progress"
                    variant="center"
                    handleCloseDialog={() => setShowLoader(false)}
                    showFooter={false}
                >
                    <ExportLoaderAndText />
                </DialogContent>
            </Dialog>
            <Dialog open={showSkipReviewDialog}>
                <DialogContent
                    title="Skip Supervisor Review"
                    handleCloseDialog={() => setShowSkipReviewDialog(false)}
                    showFooter
                    saveText="Skip review"
                    variant="center"
                    onSaveClick={handleSkipSupervisorReview}
                    cancelText="Cancel"
                    isSubmitBtnDisabled={skipSupervisorReviewMutation.isLoading}
                    isCancelBtnDisabled={skipSupervisorReviewMutation.isLoading}
                >
                    <div>
                        <h3 className="font-medium text-xl mb-8">
                            You are about to skip supervisor review and send
                            {selectedNotesDetails?.length === 1
                                ? " this note "
                                : " these notes "}
                            to code review
                        </h3>
                        <p className="text-gray font-normal text-base">
                            Do you wish to continue?
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
            <div>
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-y-[--spacing-items]">
                        <SearchInput
                            onChange={handleSetSearchString}
                            defaultValue={searchStringFilter || ""}
                            containerClass="w-[400px] max-w-full"
                            placeholder="Search by clinician"
                        />
                        <div className="flex items-center justify-between gap-[--spacing-items] flex-wrap">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Note type</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent
                                        align="end"
                                        width="auto"
                                        maxHeight={220}
                                    >
                                        {Object.values(NoteTypes).map(
                                            (type) => (
                                                <DropdownItem
                                                    key={type}
                                                    onClick={() =>
                                                        handleSetTypeFilter(
                                                            type
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={type}
                                                    values={typeFilter}
                                                >
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            NoteLabels[type]
                                                        )
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {getArchivedNoteFilterToDisplay()}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width={170}>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "all"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "all"
                                                )
                                            }
                                        >
                                            All notes
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "true"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "true"
                                                )
                                            }
                                        >
                                            Archived notes
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "false"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "false"
                                                )
                                            }
                                        >
                                            Unarchived notes
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                                {canViewFlaggedStatus ? (
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton>
                                                {getFlaggedNotesFilterTextToDisplay()}
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="end"
                                            width="auto"
                                            maxHeight={216}
                                        >
                                            <DropdownItem
                                                isSelected={
                                                    showFlaggedNotesFilter ===
                                                    "true"
                                                }
                                                onClick={() =>
                                                    handleFlaggedStatusFilterChange(
                                                        "true"
                                                    )
                                                }
                                            >
                                                Show flagged notes
                                            </DropdownItem>
                                            <DropdownItem
                                                isSelected={
                                                    showFlaggedNotesFilter ===
                                                    "false"
                                                }
                                                onClick={() =>
                                                    handleFlaggedStatusFilterChange(
                                                        "false"
                                                    )
                                                }
                                            >
                                                Hide flagged notes
                                            </DropdownItem>
                                        </DropdownContent>
                                    </Dropdown>
                                ) : null}

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>Date</FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align={
                                            screenIsGreaterThan450
                                                ? "end"
                                                : "start"
                                        }
                                    >
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateFilterToDate}
                                            onChange={handleToDateFilterChange}
                                            minDate={fromDateFilterToDate}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            {canCreateNote ? (
                                <div>
                                    <Button
                                        onClick={() => navigate("/add-note")}
                                    >
                                        Create note
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "type[]",
                                values: typeFilter.map(
                                    (type) => NoteLabels[type]
                                ),
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                    />
                </Card>
                {isLoading && <Loader />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display client notes at this time please try again later`
                        }
                    />
                )}
                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        isError={false}
                        stateHelperText="Client notes will appear here when added"
                    />
                )}
                {data && isSuccess && data.data.length > 0 && (
                    <>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 ? (
                            <div className="flex items-center mb-[--spacing-items] gap-x-[--spacing-items]">
                                <span className="font-medium text-base">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }
                                </span>
                                {canExportNotes && hasExportableSelectedNote && (
                                    <Button
                                        onClick={() => handleExportNotes()}
                                        variant="secondary"
                                    >
                                        Export
                                    </Button>
                                )}
                                {showBulkSkipSupervisorReviewBtn && (
                                    <Button
                                        onClick={() =>
                                            setShowSkipReviewDialog(true)
                                        }
                                        variant="secondary"
                                        disabled={
                                            skipSupervisorReviewMutation.isLoading
                                        }
                                    >
                                        Skip supervisor review
                                    </Button>
                                )}
                            </div>
                        ) : null}
                        <div>
                            <Table
                                data={data.data}
                                columns={columns}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                handleRowClick={handleRowClick}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                hasPagination={data.total_count > 20}
                                isColumnVisible={{
                                    flagged_for_audit_status:
                                        canViewFlaggedStatus,
                                }}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                }
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
